import { lazy, type ComponentType } from 'react';

/**
 * After a web rebuild, Vite's hashed chunk names change. A tab that was already
 * open still points at the previous files and 404s on the next lazy route.
 * Reload once so the new index.html can pick up the current bundle.
 */
const RELOAD_KEY = 'awards:stale-chunk-reload';

export function isStaleChunkError(error: unknown): boolean {
  const msg = error instanceof Error ? error.message : String(error ?? '');
  return /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Unable to preload CSS/i.test(
    msg,
  );
}

/** Returns true if a reload was triggered. False if we already tried this tab. */
export function reloadOnceForStaleChunk(): boolean {
  try {
    if (sessionStorage.getItem(RELOAD_KEY) === '1') return false;
    sessionStorage.setItem(RELOAD_KEY, '1');
  } catch {
    return false;
  }
  window.location.reload();
  return true;
}

/** Call after a successful boot so a later rebuild can recover again. */
export function clearStaleChunkReloadFlag(): void {
  try {
    sessionStorage.removeItem(RELOAD_KEY);
  } catch {
    /* private mode / blocked storage */
  }
}

export function recoverStaleChunk<T>(p: Promise<T>): Promise<T> {
  return p.catch((err: unknown) => {
    if (isStaleChunkError(err)) reloadOnceForStaleChunk();
    throw err;
  });
}

export function lazyPage(load: () => Promise<{ default: ComponentType }>) {
  return lazy(() => recoverStaleChunk(load()));
}
